import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Truck, Ship, Plane, Package, ShieldCheck, Clock, Headphones, Award, ArrowRight, Send, MapPin, CheckCircle2, Phone, Mail } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";

const HERO_BG = "https://images.unsplash.com/photo-1759826350352-c5b0b77729bd?crop=entropy&cs=srgb&fm=webp&w=1600&q=75&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHw0fHxsb2dpc3RpY3MlMjBzaGlwcGluZyUyMGNhcmdvJTIwY29udGFpbmVyJTIwc2hpcCUyMGFpcnBsYW5lJTIwdHJ1Y2slMjB3YXJlaG91c2V8ZW58MHx8fHwxNzcyNDQ4MjUyfDA&ixlib=rb-4.1.0";

const clientLogosRow1 = [
  { name: "Vishal Manufacturer Pvt. Ltd.", logo: "https://customer-assets.emergentagent.com/job_logistics-rajkot/artifacts/wjxvyrr1_vishal-manufacturer-pvt-ltd.webp" },
  { name: "Orient Star Group", logo: "https://customer-assets.emergentagent.com/job_logistics-rajkot/artifacts/l39qr2cj_685c0cef50145cdea688f400_66e959133b06600356d90f9d_orient-star-logo.png" },
  { name: "GN Altech", logo: "https://customer-assets.emergentagent.com/job_logistics-rajkot/artifacts/ms49b5i2_1a2a0c_caf29c762e75471ea0de8886c894aa84~mv2.avif" },
  { name: "K-Indev Logistics", logo: "https://customer-assets.emergentagent.com/job_logistics-rajkot/artifacts/pbtferdv_logo.jpg" },
  { name: "Geodis", logo: "https://customer-assets.emergentagent.com/job_logistics-rajkot/artifacts/z2g3tdo7_1746617717452.jfif" },
  { name: "DB Schenker", logo: "https://customer-assets.emergentagent.com/job_logistics-rajkot/artifacts/1wt9jfir_images%20%281%29.jfif" },
  { name: "Kuehne+Nagel", logo: "https://customer-assets.emergentagent.com/job_logistics-rajkot/artifacts/heq4gvq7_Kuehnenagelweb.jpg" },
  { name: "Nippon Express", logo: "https://customer-assets.emergentagent.com/job_logistics-rajkot/artifacts/x8ecgzge_logo.png" },
  { name: "CEVA Logistics", logo: "https://customer-assets.emergentagent.com/job_logistics-rajkot/artifacts/k87pu14k_images.png" },
];

const clientLogosRow2 = [
  { name: "Reliance Industries", logo: "https://customer-assets.emergentagent.com/job_logistics-rajkot/artifacts/8w93o1qb_1361860.png" },
  { name: "Macpower", logo: "https://customer-assets.emergentagent.com/job_logistics-rajkot/artifacts/h0skl6g4_images.jfif" },
  { name: "Shakti Forge Industries", logo: "https://customer-assets.emergentagent.com/job_logistics-rajkot/artifacts/gxyv6ho4_shakti-forge-industries-pvt-ltd--v2.jpg" },
  { name: "Bollore Logistics", logo: "https://customer-assets.emergentagent.com/job_logistics-rajkot/artifacts/s98qu5ay_gvexciawwdrgdo33dgag.avif" },
  { name: "DSV Air & Sea", logo: "https://customer-assets.emergentagent.com/job_logistics-rajkot/artifacts/v1zmno1m_dsv-air-and-sea-pvt-ltd.png" },
  { name: "Woco Tech", logo: "https://customer-assets.emergentagent.com/job_logistics-rajkot/artifacts/006d2u93_woco_tech_elastomere_noida_limited_logo%20%281%29.jfif" },
  { name: "DSV Global", logo: "https://customer-assets.emergentagent.com/job_logistics-rajkot/artifacts/oioot0ay_images%20%281%29.png" },
  { name: "MarineTrans", logo: "https://customer-assets.emergentagent.com/job_logistics-rajkot/artifacts/0w6yqrie_marinetrans-logo.png" },
];

const stats = [
  { value: "18+", label: "Years Experience", icon: Award },
  { value: "100K+", label: "Successful Shipments", icon: Package },
  { value: "99%", label: "On-Time Delivery", icon: Clock },
  { value: "24/7", label: "Customer Support", icon: Headphones },
];

const servicesPreview = [
  { icon: Truck, title: "In-Land Transportation", desc: "Complete inland cargo handling across India with reliable fleet management." },
  { icon: ShieldCheck, title: "Customs Clearance", desc: "Licensed customs brokerage with hassle-free documentation services." },
  { icon: Ship, title: "Ocean Freight", desc: "Competitive international shipping with full container & LCL options." },
  { icon: Plane, title: "Air Freight", desc: "Express air cargo for time-sensitive shipments worldwide." },
];

const whyChooseUs = [
  "18+ years of industry expertise",
  "Pan-India network with global reach",
  "Dedicated account managers",
  "Real-time shipment tracking",
  "Competitive and transparent pricing",
  "End-to-end supply chain solutions",
];

function useInView(ref) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref]);
  return visible;
}

function AnimatedCounter({ value, visible }) {
  const [display, setDisplay] = useState("0");
  useEffect(() => {
    if (!visible) return;
    const numMatch = value.match(/(\d+)/);
    if (!numMatch) { setDisplay(value); return; }
    const target = parseInt(numMatch[1]);
    const suffix = value.replace(numMatch[1], "");
    const duration = 1500;
    const steps = 40;
    const increment = target / steps;
    let current = 0;
    let step = 0;
    const timer = setInterval(() => {
      step++;
      current = Math.min(Math.round(increment * step), target);
      setDisplay(current >= 1000 ? `${Math.round(current / 1000)}K${suffix}` : `${current}${suffix}`);
      if (step >= steps) clearInterval(timer);
    }, duration / steps);
    return () => clearInterval(timer);
  }, [visible, value]);
  return <span>{display}</span>;
}

export default function HomePage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [errors, setErrors] = useState({});
  const statsRef = useRef(null);
  const statsVisible = useInView(statsRef);

  const validateForm = () => {
    const errs = {};
    if (!form.name.trim() || form.name.trim().length < 2) errs.name = "Name is required (min 2 characters)";
    if (!form.email.trim()) errs.email = "Email is required";
    else if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(form.email)) errs.email = "Please enter a valid email";
    if (!form.message.trim() || form.message.trim().length < 10) errs.message = "Message is required (min 10 characters)";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    const text = `Hello Pranav Logistics,\nI would like to request a quote.\n\nName: ${form.name.trim()}\nEmail: ${form.email.trim()}\nPhone: ${form.phone.trim() || "Not provided"}\nMessage: ${form.message.trim()}`;
    const url = `https://wa.me/917575873232?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank");
  };

  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-[85vh] flex items-center" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="Logistics operations" className="w-full h-full object-cover" loading="eager" fetchPriority="high" />
          <div className="hero-overlay absolute inset-0" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-3xl">
            <div className="inline-block bg-[#F97316]/10 border border-[#F97316]/30 rounded-full px-4 py-1.5 mb-6 animate-fade-in">
              <span className="text-[#F97316] text-sm font-semibold">Trusted Logistics Since 2007</span>
            </div>
            <h1 className="font-['Barlow_Condensed'] font-extrabold text-4xl sm:text-5xl lg:text-7xl text-white leading-[1.1] mb-6 animate-fade-in-up" data-testid="hero-heading">
              Reliable Logistics Partner Delivering{" "}
              <span className="text-[#F97316]">Seamless Cargo</span> Solutions
            </h1>
            <p className="text-slate-300 text-base md:text-lg max-w-xl mb-8 animate-fade-in-up stagger-2">
              From inland transportation to ocean freight, we handle every aspect of your supply chain with precision and care.
            </p>
            <div className="flex flex-wrap gap-4 animate-fade-in-up stagger-3">
              <Link to="/services" className="btn-primary inline-flex items-center gap-2" data-testid="hero-explore-services-btn">
                Explore Services <ArrowRight size={18} />
              </Link>
              <Link to="/contact" className="btn-outline !border-white !text-white hover:!bg-white hover:!text-[#0F172A] inline-flex items-center gap-2" data-testid="hero-contact-btn">
                Get a Free Quote
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section ref={statsRef} className="bg-white py-16 md:py-20 border-b border-slate-100" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {stats.map((stat, i) => (
              <div key={stat.label} className={`stat-card bg-[#F8FAFC] p-6 md:p-8 rounded-lg border-l-4 border-[#F97316] ${statsVisible ? "animate-fade-in-up" : "opacity-0"}`} style={{ animationDelay: `${i * 0.15}s` }} data-testid={`stat-card-${i}`}>
                <stat.icon size={28} strokeWidth={1.5} className="text-[#0EA5E9] mb-3" />
                <div className="font-['Barlow_Condensed'] font-extrabold text-3xl md:text-4xl text-[#0F172A] mb-1">
                  <AnimatedCounter value={stat.value} visible={statsVisible} />
                </div>
                <div className="text-slate-500 text-sm font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT BRIEF */}
      <section className="py-16 md:py-24 bg-white" data-testid="about-brief-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <span className="text-[#F97316] font-semibold text-sm uppercase tracking-widest">About Us</span>
              <h2 className="font-['Barlow_Condensed'] font-bold text-3xl sm:text-4xl lg:text-5xl text-[#0F172A] mt-3 mb-6">
                Your Trusted Logistics Partner in India
              </h2>
              <div className="section-divider mb-6" />
              <p className="text-slate-600 leading-relaxed mb-4">
                With over 18 years of experience, Pranav Logistics has established itself as a leading logistics service provider in Rajkot, Gujarat. We specialize in delivering comprehensive cargo solutions that span from inland transportation to international freight forwarding.
              </p>
              <p className="text-slate-600 leading-relaxed mb-8">
                Our team of experienced professionals ensures that every shipment is handled with utmost care, precision, and timeliness. We are committed to providing cost-effective and reliable logistics solutions that drive your business forward.
              </p>
              <Link to="/about" className="btn-primary inline-flex items-center gap-2" data-testid="about-learn-more-btn">
                Learn More <ArrowRight size={18} />
              </Link>
            </div>
            <div className="relative">
              <div className="bg-[#F8FAFC] rounded-lg p-8 md:p-10 border border-slate-200">
                <div className="grid grid-cols-2 gap-6">
                  {whyChooseUs.slice(0, 4).map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <CheckCircle2 size={20} className="text-[#0EA5E9] mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-[#0F172A] font-medium">{item}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-8 pt-8 border-t border-slate-200 flex items-center gap-4">
                  <div className="bg-[#F97316] rounded-md p-3">
                    <MapPin size={24} className="text-white" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-[#0F172A]">Head Office</div>
                    <div className="text-sm text-slate-500">150 Feet Ring Rd, Rajkot, Gujarat</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES PREVIEW */}
      <section className="py-16 md:py-24 bg-[#F8FAFC]" data-testid="services-preview-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="text-[#F97316] font-semibold text-sm uppercase tracking-widest">What We Offer</span>
            <h2 className="font-['Barlow_Condensed'] font-bold text-3xl sm:text-4xl lg:text-5xl text-[#0F172A] mt-3 mb-4">
              Our Core Services
            </h2>
            <div className="section-divider mx-auto" />
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {servicesPreview.map((service, i) => (
              <div key={service.title} className="service-card group bg-white border border-slate-200 hover:border-[#0EA5E9] rounded-lg p-6 flex flex-col" data-testid={`service-preview-card-${i}`}>
                <div className="bg-[#F1F5F9] rounded-lg p-4 w-fit mb-5 group-hover:bg-[#FFF7ED] transition-colors duration-300">
                  <service.icon size={32} strokeWidth={1.5} className="service-icon text-[#0EA5E9]" />
                </div>
                <h3 className="font-['Barlow_Condensed'] font-bold text-xl text-[#0F172A] mb-2">{service.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed flex-grow">{service.desc}</p>
                <Link to="/services" className="mt-4 text-[#F97316] text-sm font-semibold inline-flex items-center gap-1 hover:gap-2 transition-all">
                  Learn More <ArrowRight size={14} />
                </Link>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/services" className="btn-secondary inline-flex items-center gap-2" data-testid="view-all-services-btn">
              View All Services <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="py-16 md:py-24 bg-white" data-testid="why-choose-us-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="order-2 lg:order-1">
              <div className="bg-gradient-to-br from-[#0F172A] to-[#1E293B] rounded-lg p-8 md:p-12 text-white">
                <h3 className="font-['Barlow_Condensed'] font-bold text-2xl mb-8">Why Businesses Trust Us</h3>
                <div className="space-y-5">
                  {whyChooseUs.map((item, i) => (
                    <div key={i} className="flex items-center gap-4">
                      <div className="w-8 h-8 rounded-full bg-[#F97316]/20 flex items-center justify-center flex-shrink-0">
                        <CheckCircle2 size={16} className="text-[#F97316]" />
                      </div>
                      <span className="text-slate-200 text-sm">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <span className="text-[#F97316] font-semibold text-sm uppercase tracking-widest">Why Choose Us</span>
              <h2 className="font-['Barlow_Condensed'] font-bold text-3xl sm:text-4xl lg:text-5xl text-[#0F172A] mt-3 mb-6">
                Delivering Excellence in Every Shipment
              </h2>
              <div className="section-divider mb-6" />
              <p className="text-slate-600 leading-relaxed mb-4">
                At Pranav Logistics, we understand that every shipment is critical to your business. That's why we combine cutting-edge technology with decades of expertise to ensure your cargo reaches its destination safely and on time.
              </p>
              <p className="text-slate-600 leading-relaxed">
                Our comprehensive network spans across major ports, airports, and inland destinations throughout India, enabling us to provide seamless logistics solutions tailored to your specific needs.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* TRUSTED CLIENTS */}
      <section className="py-16 md:py-24 bg-[#F8FAFC] overflow-hidden" data-testid="trusted-clients-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-[#F97316] font-semibold text-sm uppercase tracking-widest">Our Partners</span>
            <h2 className="font-['Barlow_Condensed'] font-bold text-3xl sm:text-4xl lg:text-5xl text-[#0F172A] mt-3 mb-4">
              Trusted Clients
            </h2>
            <div className="section-divider mx-auto mb-4" />
            <p className="text-slate-500 text-sm max-w-lg mx-auto">
              Proud to partner with leading businesses across industries for their logistics needs
            </p>
          </div>
        </div>
        {/* Row 1 - scrolls left */}
        <div className="marquee-container mb-5">
          <div className="marquee-track">
            {[...clientLogosRow1, ...clientLogosRow1, ...clientLogosRow1].map((client, i) => (
              <div key={`r1-${i}`} className="client-logo-card" data-testid={`client-logo-r1-${i}`}>
                <img src={client.logo} alt={client.name} loading="lazy" title={client.name} />
              </div>
            ))}
          </div>
        </div>
        {/* Row 2 - scrolls right */}
        <div className="marquee-container">
          <div className="marquee-track marquee-reverse">
            {[...clientLogosRow2, ...clientLogosRow2, ...clientLogosRow2].map((client, i) => (
              <div key={`r2-${i}`} className="client-logo-card" data-testid={`client-logo-r2-${i}`}>
                <img src={client.logo} alt={client.name} loading="lazy" title={client.name} />
              </div>
            ))}
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-10 text-center">
          <p className="text-slate-400 text-xs font-medium">17+ Trusted Partners & Growing</p>
        </div>
      </section>

      {/* QUICK CONTACT */}
      <section className="py-16 md:py-24 bg-white" data-testid="quick-contact-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
            <div>
              <span className="text-[#F97316] font-semibold text-sm uppercase tracking-widest">Get In Touch</span>
              <h2 className="font-['Barlow_Condensed'] font-bold text-3xl sm:text-4xl lg:text-5xl text-[#0F172A] mt-3 mb-6">
                Request a Free Quote
              </h2>
              <div className="section-divider mb-6" />
              <p className="text-slate-600 leading-relaxed mb-8">
                Have a shipment that needs moving? Get in touch with our team for a customized logistics solution and competitive pricing.
              </p>
              <div className="space-y-4">
                <a href="tel:+917575873232" className="flex items-center gap-4 text-[#0F172A] hover:text-[#F97316] transition-colors">
                  <div className="bg-[#F1F5F9] p-3 rounded-lg"><Phone size={20} className="text-[#0EA5E9]" /></div>
                  <div>
                    <div className="text-xs text-slate-500">Call Us</div>
                    <div className="font-semibold text-sm">+91 75758 73232</div>
                  </div>
                </a>
                <a href="mailto:info@pranavlogistics.com?subject=Inquiry%20-%20Pranav%20Logistics" className="flex items-center gap-4 text-[#0F172A] hover:text-[#F97316] transition-colors">
                  <div className="bg-[#F1F5F9] p-3 rounded-lg"><Mail size={20} className="text-[#0EA5E9]" /></div>
                  <div>
                    <div className="text-xs text-slate-500">Email Us</div>
                    <div className="font-semibold text-sm">info@pranavlogistics.com</div>
                  </div>
                </a>
              </div>
            </div>
            <div>
              <form onSubmit={handleSubmit} className="bg-[#F8FAFC] rounded-lg p-6 md:p-8 border border-slate-200 space-y-5" data-testid="home-contact-form">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="text-sm font-medium text-[#0F172A] mb-1.5 block">Name *</label>
                    <Input
                      placeholder="Your full name"
                      value={form.name}
                      onChange={(e) => { setForm({ ...form, name: e.target.value }); setErrors({ ...errors, name: "" }); }}
                      className={`bg-white h-12 ${errors.name ? "border-red-400 focus:border-red-400" : "border-slate-300 focus:border-[#0EA5E9]"}`}
                      data-testid="home-contact-name"
                    />
                    {errors.name && <p className="text-red-500 text-xs mt-1" data-testid="home-contact-name-error">{errors.name}</p>}
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[#0F172A] mb-1.5 block">Email *</label>
                    <Input
                      type="email"
                      placeholder="your@email.com"
                      value={form.email}
                      onChange={(e) => { setForm({ ...form, email: e.target.value }); setErrors({ ...errors, email: "" }); }}
                      className={`bg-white h-12 ${errors.email ? "border-red-400 focus:border-red-400" : "border-slate-300 focus:border-[#0EA5E9]"}`}
                      data-testid="home-contact-email"
                    />
                    {errors.email && <p className="text-red-500 text-xs mt-1" data-testid="home-contact-email-error">{errors.email}</p>}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-[#0F172A] mb-1.5 block">Phone</label>
                  <Input
                    placeholder="Your phone number"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="bg-white border-slate-300 focus:border-[#0EA5E9] h-12"
                    data-testid="home-contact-phone"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#0F172A] mb-1.5 block">Message *</label>
                  <Textarea
                    placeholder="Tell us about your logistics needs..."
                    rows={4}
                    value={form.message}
                    onChange={(e) => { setForm({ ...form, message: e.target.value }); setErrors({ ...errors, message: "" }); }}
                    className={`bg-white ${errors.message ? "border-red-400 focus:border-red-400" : "border-slate-300 focus:border-[#0EA5E9]"}`}
                    data-testid="home-contact-message"
                  />
                  {errors.message && <p className="text-red-500 text-xs mt-1" data-testid="home-contact-message-error">{errors.message}</p>}
                </div>
                <button
                  type="submit"
                  className="btn-primary w-full flex items-center justify-center gap-2"
                  data-testid="home-contact-submit-btn"
                >
                  <Send size={18} /> Send via WhatsApp
                </button>
                <p className="text-slate-400 text-xs text-center">Your details will be sent to our WhatsApp for a quick response</p>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
